import { useMemo } from 'react'
import { useAdminAuth } from '@/contexts/AdminAuthContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faHome,
  faUsers,
  faCreditCard,
  faCog,
  faClipboardList,
  faTableList,
  faChartLine,
  faWrench,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons'

type NavigationItem = {
  name: string
  href: string
  icon: React.ComponentProps<typeof FontAwesomeIcon>['icon']
  permission?: string
  children?: NavigationItem[]
}

export function useAdminNavigation() {
  const { hasPermission } = useAdminAuth()

  const navigation: NavigationItem[] = useMemo(() => ([
    { name: 'Dashboard', href: '/', icon: faHome, permission: 'dashboard:view' },
    { name: 'Tenants', href: '/tenants', icon: faUsers, permission: 'tenants:view' },
    { name: 'Plans', href: '/plans', icon: faTableList, permission: 'plans:view' },
    { name: 'Subscriptions', href: '/subscriptions', icon: faCreditCard, permission: 'subscriptions:view' },
    { name: 'Monitoring', href: '/monitoring', icon: faChartLine, permission: 'monitoring:view' },
    {
      name: 'Settings',
      href: '#',
      icon: faWrench,
      permission: 'settings:view',
      children: [
        { name: 'System Settings', href: '/system-settings', icon: faCog, permission: 'settings:view' },
        { name: 'Users', href: '/admin-users', icon: faUsers, permission: 'admin:users:view' },
        { name: 'Roles & Permissions', href: '/admin-roles', icon: faShieldHalved, permission: 'admin:roles:view' },
        { name: 'Audit Log', href: '/audit-log', icon: faClipboardList, permission: 'audit:view' },
      ],
    },
  ]), [])

  const filtered = useMemo(() => {
    const canSee = (item: NavigationItem) => !item.permission || hasPermission(item.permission)
    return navigation
      .map((item) => {
        if (item.children && item.children.length > 0) {
          const children = item.children.filter(canSee)
          if (children.length === 0) return null
          return { ...item, children }
        }
        return canSee(item) ? item : null
      })
      .filter((x): x is NavigationItem => Boolean(x))
  }, [navigation, hasPermission])

  return { navigation: filtered }
}


